import React from 'react';
import { Dialog } from '@headlessui/react';
import { Star } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link } from 'react-router-dom';

interface Review {
  id: string;
  productId: string;
  productName?: string;
  productImage?: string;
  userId: string;
  userName: string;
  userPhoto?: string;
  rating: number;
  comment: string;
  status: string;
  moderationStatus: string | null;
  moderatedBy?: string;
  moderatedAt?: string;
  moderationFeedback?: string;
  createdAt: string;
  updatedAt: string;
  orderId: string;
}

interface ReviewDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  review: Review;
}

export default function ReviewDetailModal({ isOpen, onClose, review }: ReviewDetailModalProps) {
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-lg shadow-xl">
          <div className="p-6">
            <Dialog.Title className="text-lg font-medium mb-4">
              Detalhes da Review
            </Dialog.Title>

            <div className="space-y-4">
              {/* Produto */}
              <div className="flex items-center space-x-4">
                {review.productImage && (
                  <img 
                    src={review.productImage} 
                    alt={review.productName} 
                    className="w-16 h-16 object-cover rounded-md"
                  />
                )}
                <div>
                  <h3 className="font-medium">{review.productName}</h3>
                  <p className="text-sm text-gray-500">ID: {review.productId}</p>
                </div>
              </div>

              {/* Cliente */}
              <div>
                <h4 className="font-medium mb-1">Cliente</h4>
                <div className="flex items-center space-x-3">
                  {review.userPhoto ? (
                    <img 
                      src={review.userPhoto} 
                      alt={review.userName} 
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium">
                        {review.userName.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-medium">{review.userName}</p>
                    <p className="text-sm text-gray-500">ID: {review.userId}</p>
                  </div>
                </div>
              </div>

              {/* Avaliação */}
              <div>
                <h4 className="font-medium mb-1">Avaliação</h4>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i < review.rating
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                  <span className="ml-2 text-sm text-gray-600">
                    {review.rating} de 5
                  </span>
                </div>
              </div>

              {/* Comentário */}
              <div>
                <h4 className="font-medium mb-1">Comentário</h4>
                <p className="text-gray-700">{review.comment}</p>
              </div>

              {/* Status */}
              <div>
                <h4 className="font-medium mb-1">Status</h4>
                <div className="space-y-2">
                  <p>
                    <span className="text-gray-500">Status:</span>{' '}
                    <span className={`font-medium ${
                      review.status === 'active' ? 'text-green-600' :
                      review.status === 'pending' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {review.status === 'active' ? 'Ativo' :
                       review.status === 'pending' ? 'Pendente' :
                       'Rejeitado'}
                    </span>
                  </p>
                  <p>
                    <span className="text-gray-500">Moderação:</span>{' '}
                    <span className={`font-medium ${
                      review.moderationStatus === 'approved' ? 'text-green-600' :
                      review.moderationStatus === 'rejected' ? 'text-red-600' :
                      'text-yellow-600'
                    }`}>
                      {review.moderationStatus === 'approved' ? 'Aprovada' :
                       review.moderationStatus === 'rejected' ? 'Rejeitada' :
                       'Pendente'}
                    </span>
                  </p>
                  {review.moderatedBy && (
                    <p>
                      <span className="text-gray-500">Moderado por:</span>{' '}
                      <span className="font-medium">
                        {review.moderatedBy === 'system' ? 'Sistema' : review.moderatedBy}
                      </span>
                    </p>
                  )}
                  {review.moderatedAt && (
                    <p>
                      <span className="text-gray-500">Data da moderação:</span>{' '}
                      <span className="font-medium">
                        {format(new Date(review.moderatedAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
                      </span>
                    </p>
                  )}
                  {review.moderationFeedback && (
                    <p>
                      <span className="text-gray-500">Feedback da moderação:</span>{' '}
                      <span className="font-medium">{review.moderationFeedback}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Ordem */}
              <div>
                <h4 className="font-medium mb-1">Ordem</h4>
                <div className="flex items-center justify-between">
                  <p className="text-gray-700">
                    <span className="text-gray-500">Número da Ordem:</span>{' '}
                    <span className="font-medium">{review.orderId}</span>
                  </p>
                  <Link
                    to={`/dashboard/orders/${review.orderId}`}
                    className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100"
                  >
                    Ver Ordem
                    <svg
                      className="w-4 h-4 ml-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </Link>
                </div>
              </div>

              {/* Datas */}
              <div>
                <h4 className="font-medium mb-1">Datas</h4>
                <div className="space-y-1 text-sm text-gray-500">
                  <p>
                    <span>Criado em:</span>{' '}
                    {format(new Date(review.createdAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                  <p>
                    <span>Última atualização:</span>{' '}
                    {format(new Date(review.updatedAt), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              >
                Fechar
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
